import React, { useCallback, useEffect, useState } from "react";
import { Flag, MicOff, Paperclip, ShieldCheck, Trash2, Undo2, XCircle } from "lucide-react";
import CommonToaster from "../Common/CommonToaster";
import {
  ActionMenu, ActionMenuDivider, ActionMenuItem, AppDataTable, ConfirmModal, PageHeader,
  StatsCard, StatsGrid, StatusBadge, Tabs, TabsContent, TabsList, TabsTrigger,
} from "../ui";
import {
  getChatMutes, getChatReports, resolveChatReport, unmuteChatMember,
} from "../../utils/chatModerationApi";

/**
 * ChatReportsPage — the institute's queue of reported batch chat messages.
 *
 * Students report a message (and can block its sender) from the mobile app;
 * this is where an admin decides. It exists because App Store Guideline 1.2
 * requires reports on user-generated content to be acted on — remove the
 * content, and be able to stop the person posting — and the people who can
 * judge a message in a batch are the institute that runs the batch.
 *
 * ⚠️ SERVER-PAGINATED. The status column filter goes upstream as `status`,
 * and it defaults to open reports, because the notification that brings an
 * admin here is about one that needs a decision.
 */

const PAGE_SIZE = 20;

const REASON_LABEL = {
  spam: "Spam",
  harassment: "Bullying or harassment",
  hate: "Hate speech",
  sexual: "Sexual content",
  violence: "Violence or threats",
  other: "Something else",
};

const STATUS_OPTIONS = [
  { value: "open", label: "Needs review" },
  { value: "removed", label: "Removed" },
  { value: "dismissed", label: "Dismissed" },
];

const when = (d) => (d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

/** Dismiss needs no confirm: it changes nothing anyone in the chat can see. */
const CONFIRM = {
  remove: {
    title: "Remove this message?",
    confirmText: "Remove message",
    message: (r) => `It is deleted for everyone in ${r.batchName || "the batch"}. ${r.snapshot?.senderName || "The sender"} can keep posting.`,
  },
  remove_and_mute: {
    title: "Remove and mute the sender?",
    confirmText: "Remove and mute",
    message: (r) => `The message is deleted for everyone, and ${r.snapshot?.senderName || "the sender"} can no longer post in any of this institute's batch chats. They keep their classes and can still read chat. You can undo the mute on the Muted members tab.`,
  },
};

const DONE = {
  remove: "Message removed.",
  remove_and_mute: "Message removed and the sender muted.",
  dismiss: "Report dismissed. The message stays.",
};

const ChatReportsPage = () => {
  const [tab, setTab] = useState("reports");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ open: 0, removed: 0, dismissed: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [columnFilters, setColumnFilters] = useState({ status: ["open"] });

  const [mutes, setMutes] = useState([]);
  const [mutesLoading, setMutesLoading] = useState(true);

  const [confirm, setConfirm] = useState(null); // { action, row }
  const [acting, setActing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = useCallback((message, type = "success") => setToast({ show: true, message, type }), []);

  const statusParam = (columnFilters.status || []).join(",");

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getChatReports({ status: statusParam || undefined, page, pageSize: PAGE_SIZE });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      if (data.counts) setCounts(data.counts);
    } catch (err) {
      setRows([]);
      setTotal(0);
      showToast(err?.response?.data?.message || "Failed to load chat reports.", "error");
    } finally {
      setLoading(false);
    }
  }, [page, statusParam, showToast]);

  const loadMutes = useCallback(async () => {
    setMutesLoading(true);
    try {
      const { data } = await getChatMutes();
      setMutes(Array.isArray(data) ? data : []);
    } catch (err) {
      setMutes([]);
      showToast(err?.response?.data?.message || "Failed to load muted members.", "error");
    } finally {
      setMutesLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => { loadMutes(); }, [loadMutes]);

  const act = async (action, row) => {
    setActing(true);
    try {
      await resolveChatReport(row._id, action);
      showToast(DONE[action]);
      setConfirm(null);
      loadReports();
      if (action === "remove_and_mute") loadMutes();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to update the report.", "error");
    } finally {
      setActing(false);
    }
  };

  const unmute = async (userId, name) => {
    try {
      await unmuteChatMember(userId);
      showToast(`${name || "They"} can post in chat again.`);
      loadMutes();
      loadReports();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to unmute.", "error");
    }
  };

  const reportColumns = [
    {
      key: "message",
      label: "Message",
      render: (r) => (
        <div className="tw-min-w-0 tw-max-w-md">
          <div className="tw-truncate tw-text-sm tw-font-medium tw-text-content">
            {r.snapshot?.senderName || "Unknown"}
            {r.batchName && <span className="tw-font-normal tw-text-content-subtle"> · {r.batchName}</span>}
          </div>
          {r.snapshot?.text && (
            <p className="tw-mb-0 tw-mt-0.5 tw-line-clamp-2 tw-text-sm tw-text-content-muted">{r.snapshot.text}</p>
          )}
          {r.snapshot?.attachmentCount > 0 && (
            <span className="tw-mt-0.5 tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-content-subtle">
              <Paperclip size={12} aria-hidden="true" />
              {r.snapshot.attachmentCount === 1 ? "1 attachment" : `${r.snapshot.attachmentCount} attachments`}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "reason",
      label: "Reason",
      render: (r) => (
        <div className="tw-min-w-0 tw-max-w-xs">
          <div className="tw-text-sm tw-text-content">{REASON_LABEL[r.reason] || r.reason}</div>
          {r.details && (
            <div className="tw-line-clamp-2 tw-text-xs tw-text-content-subtle" title={r.details}>{r.details}</div>
          )}
        </div>
      ),
    },
    {
      key: "reportedBy",
      label: "Reported by",
      render: (r) => (
        <div>
          <div className="tw-text-sm tw-text-content">{r.reporterName || "—"}</div>
          <div className="tw-text-xs tw-text-content-subtle">{when(r.createdAt)}</div>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      filter: { type: "set", options: STATUS_OPTIONS },
      render: (r) => (
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
          <StatusBadge status={r.status} domain="chatReport" />
          {r.senderMuted && <span className="tw-text-xs tw-text-content-subtle">Sender muted</span>}
        </div>
      ),
    },
    {
      key: "actions",
      label: "",
      width: 48,
      pinned: "right",
      resizable: false,
      render: (r) => {
        const sender = r.snapshot?.senderName || "the sender";
        if (r.status === "open") {
          return (
            <ActionMenu label={`Actions for the report on ${sender}'s message`}>
              <ActionMenuItem onClick={() => setConfirm({ action: "remove", row: r })}>
                <Trash2 size={14} /> Remove message
              </ActionMenuItem>
              <ActionMenuItem danger onClick={() => setConfirm({ action: "remove_and_mute", row: r })}>
                <MicOff size={14} /> Remove and mute sender
              </ActionMenuItem>
              <ActionMenuDivider />
              <ActionMenuItem onClick={() => act("dismiss", r)}>
                <XCircle size={14} /> Dismiss report
              </ActionMenuItem>
            </ActionMenu>
          );
        }
        if (r.senderMuted) {
          return (
            <ActionMenu label={`Actions for ${sender}`}>
              <ActionMenuItem onClick={() => unmute(r.reportedUserId, sender)}>
                <Undo2 size={14} /> Unmute sender
              </ActionMenuItem>
            </ActionMenu>
          );
        }
        return null;
      },
    },
  ];

  const muteColumns = [
    {
      key: "userName",
      label: "Member",
      render: (m) => <span className="tw-text-sm tw-font-medium tw-text-content">{m.userName || "Unknown"}</span>,
    },
    {
      key: "mutedBy",
      label: "Muted by",
      render: (m) => (
        <div>
          <div className="tw-text-sm tw-text-content">{m.mutedBy || "—"}</div>
          <div className="tw-text-xs tw-text-content-subtle">{when(m.mutedAt)}</div>
        </div>
      ),
    },
    {
      key: "actions",
      label: "",
      width: 48,
      pinned: "right",
      resizable: false,
      render: (m) => (
        <ActionMenu label={`Actions for ${m.userName || "this member"}`}>
          <ActionMenuItem onClick={() => unmute(m.userId, m.userName)}>
            <Undo2 size={14} /> Unmute
          </ActionMenuItem>
        </ActionMenu>
      ),
    },
  ];

  const pending = confirm ? CONFIRM[confirm.action] : null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <PageHeader
        icon={Flag}
        title="Chat Reports"
        description="Messages your students reported in batch chat. Review each one within a day: remove it, mute the sender, or dismiss the report."
      />

      <StatsGrid cols={3}>
        <StatsCard title="Needs review" value={counts.open} icon={Flag} iconColor="amber" />
        <StatsCard title="Removed" value={counts.removed} icon={Trash2} iconColor="emerald" />
        <StatsCard title="Dismissed" value={counts.dismissed} icon={ShieldCheck} iconColor="blue" />
      </StatsGrid>

      <Tabs value={tab} onChange={setTab}>
        <TabsList>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="muted">Muted members{mutes.length ? ` (${mutes.length})` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="reports">
          <AppDataTable
            columns={reportColumns}
            rows={rows}
            rowKey={(r) => r._id}
            loading={loading}
            fill={false}
            columnFilters={columnFilters}
            onColumnFilterChange={(key, value) => {
              setColumnFilters((prev) => ({ ...prev, [key]: value }));
              setPage(1);
            }}
            emptyMessage={
              statusParam === "open"
                ? "No reports need review. Reports your students send from the app appear here."
                : "No reports match this filter."
            }
            pagination={{
              page,
              total,
              pageSize: PAGE_SIZE,
              totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
              onChange: setPage,
            }}
          />
        </TabsContent>

        <TabsContent value="muted">
          <AppDataTable
            columns={muteColumns}
            rows={mutes}
            rowKey={(m) => m.userId}
            loading={mutesLoading}
            fill={false}
            emptyMessage="Nobody is muted. Muting a sender from a report stops them posting in every batch chat."
          />
        </TabsContent>
      </Tabs>

      <ConfirmModal
        show={!!confirm}
        onHide={() => !acting && setConfirm(null)}
        onConfirm={() => confirm && act(confirm.action, confirm.row)}
        title={pending?.title}
        message={confirm && pending ? pending.message(confirm.row) : ""}
        confirmText={pending?.confirmText}
        variant="danger"
        loading={acting}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default ChatReportsPage;
